import React, { useCallback, useEffect, useRef } from 'react'
import clsx from 'clsx'
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material'
import { DatePicker, Icon } from 'src/components/atoms'
import CenteringLoading from 'src/components/molecules/CenteringLoading'
import {
  ActivityLogDateRangeFilter,
  EVENT_TYPE_LABELS,
} from 'src/domain/activityLog'
import { ActivityLog } from 'src/slices/services/api'
import ActivityLogItem from '../ActivityLogItem'
import styles from './styles.module.scss'

export type ActivityLogPanelProps = {
  orgCode: string
  open: boolean
  title: string
  logs: ActivityLog[]
  isLoading?: boolean
  isLoadingMore?: boolean
  hasMore?: boolean
  eventTypeLabels?: typeof EVENT_TYPE_LABELS
  showDiff?: boolean
  dateRangeFilter?: ActivityLogDateRangeFilter
  // 取得エラー時にパネル内へ表示する文言 (閉じずにその場で直せるようにするため)
  errorMessage?: string
  onClose: () => void
  onLoadMore?: () => void
} & React.StyledProps

const ActivityLogPanel: React.StyledFC<ActivityLogPanelProps> = ({
  className,
  orgCode,
  open,
  title,
  logs,
  isLoading,
  isLoadingMore,
  hasMore,
  eventTypeLabels,
  showDiff = true,
  dateRangeFilter,
  errorMessage,
  onClose,
  onLoadMore,
}: ActivityLogPanelProps) => {
  const bottomBoundaryRef = useRef<HTMLDivElement>(null)

  const handleOnReachToBottom = useCallback(() => {
    if (hasMore && !isLoadingMore && onLoadMore) {
      onLoadMore()
    }
  }, [hasMore, isLoadingMore, onLoadMore])

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        handleOnReachToBottom()
      }
    })
    if (bottomBoundaryRef.current) {
      observer.observe(bottomBoundaryRef.current)
    }
    return () => observer.disconnect()
  }, [handleOnReachToBottom])

  const renderContent = () => {
    if (isLoading) return <CenteringLoading />
    if (errorMessage)
      return (
        <Typography className={styles.errorMessage}>{errorMessage}</Typography>
      )
    if (logs.length === 0)
      return (
        <Typography className={styles.emptyMessage}>
          履歴がありません
        </Typography>
      )
    return (
      <>
        {logs.map((log) => (
          <ActivityLogItem
            key={log.operationId}
            orgCode={orgCode}
            log={log}
            eventTypeLabel={
              showDiff
                ? eventTypeLabels?.[log.entityType]?.[log.eventType]
                : undefined
            }
            showDiff={showDiff}
          />
        ))}
        {hasMore && (
          <Box ref={bottomBoundaryRef} className={styles.bottomBoundary}>
            {isLoadingMore && (
              <CircularProgress size={24} className={styles.loadingMore} />
            )}
          </Box>
        )}
      </>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className={clsx(styles.drawer, className)}
      PaperProps={{ className: styles.paper }}
    >
      <Box className={styles.header}>
        <Typography variant="h2" className={styles.title}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          className={styles.closeButton}
        >
          <Icon icon="close" />
        </IconButton>
      </Box>
      {dateRangeFilter && (
        <Box className={styles.dateRange}>
          <DatePicker
            date={dateRangeFilter.range.start}
            minDate={dateRangeFilter.minDate}
            maxDate={dateRangeFilter.range.end}
            onChange={(start) =>
              dateRangeFilter.onChange({ ...dateRangeFilter.range, start })
            }
          />
          <Typography component="span" className={styles.dateRangeSeparator}>
            〜
          </Typography>
          <DatePicker
            date={dateRangeFilter.range.end}
            minDate={dateRangeFilter.range.start}
            maxDate={dateRangeFilter.maxDate}
            onChange={(end) =>
              dateRangeFilter.onChange({ ...dateRangeFilter.range, end })
            }
          />
        </Box>
      )}
      <Box className={styles.content}>{renderContent()}</Box>
    </Drawer>
  )
}

export default ActivityLogPanel
